import {
    bucharestSectorAt,
    bucharestSectorFromPostcode,
} from './bucharestSectors';
import catalogueJson from './romanianLocalities.data.js';

interface RomanianLocalityCatalogue {
    counties: string[];
    localities: Record<string, string[]>;
}

const catalogue = catalogueJson as RomanianLocalityCatalogue;

export const ROMANIAN_BILLING_COUNTRY_CODE = 'RO';
export const ROMANIAN_COUNTIES = catalogue.counties;
export const ROMANIAN_LOCALITIES_BY_COUNTY = catalogue.localities;

const FOLD: Record<string, string> = {
    ă: 'a', â: 'a', î: 'i', ș: 's', ş: 's', ț: 't', ţ: 't',
    Ă: 'a', Â: 'a', Î: 'i', Ș: 's', Ş: 's', Ț: 't', Ţ: 't',
};

const foldPlace = (value: string): string => Array.from(value)
    .map((char) => FOLD[char] || char)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const lookupKey = (value: string): string => {
    let text = foldPlace(value)
        .replace(/^(judetul|judet|municipiul|municipiu|mun|orasul|oras|comuna|county of|county)\s+/, '')
        .replace(/\s+(county|judet)$/, '');
    if (text === 'bucharest' || text === 'bucharest municipality') {
        text = 'bucuresti';
    }
    const sector = /^sector(?:ul)?\s*([1-6])$/.exec(text);
    if (sector) {
        text = `sector ${sector[1]}`;
    }
    return text;
};

const countyByKey = new Map<string, string>();
ROMANIAN_COUNTIES.forEach((county) => {
    countyByKey.set(lookupKey(county), county);
});

const localitiesByKey = new Map<string, Map<string, string[]>>();
ROMANIAN_COUNTIES.forEach((county) => {
    const byKey = new Map<string, string[]>();
    ROMANIAN_LOCALITIES_BY_COUNTY[county].forEach((locality) => {
        const key = lookupKey(locality.replace(/\s+\([^)]*\)$/, ''));
        const matches = byKey.get(key) || [];
        matches.push(locality);
        byKey.set(key, matches);
    });
    localitiesByKey.set(county, byKey);
});

export const billingLocalityName = (value: string): string => value.replace(/\s+\([^)]*\)$/, '').trim();

export const isRomanianCounty = (value: string): boolean => ROMANIAN_COUNTIES.includes(value);

export const isRomanianLocality = (county: string, value: string): boolean => (
    ROMANIAN_LOCALITIES_BY_COUNTY[county] || []
).includes(value);

export const matchRomanianCounty = (value: string): string => countyByKey.get(lookupKey(value)) || '';

const parentName = (locality: string): string => {
    const open = locality.lastIndexOf('(');
    if (open < 0 || !locality.endsWith(')')) {
        return '';
    }
    return locality.slice(open + 1, -1);
};

export const matchRomanianLocality = (
    county: string,
    value: string,
    parents: string[] = [],
): string => {
    const matches = localitiesByKey.get(county)?.get(lookupKey(value));
    if (!matches?.length) {
        return '';
    }
    for (const parent of parents) {
        const parentKey = lookupKey(parent);
        const qualified = matches.find((locality) => lookupKey(parentName(locality)) === parentKey);
        if (qualified) {
            return qualified;
        }
    }
    if (matches.length === 1) {
        return matches[0];
    }
    return matches.find((locality) => !parentName(locality)) || '';
};

export const preselectRomanianAddress = (parts: {
    countryCode?: string;
    region?: string;
    city?: string;
    locality?: string;
    district?: string;
    placeName?: string;
    postalCode?: string;
    longitude?: number;
    latitude?: number;
}): { countryCode: string; region: string; city: string } => {
    const countryCode = parts.countryCode?.trim().toUpperCase() === ROMANIAN_BILLING_COUNTRY_CODE
        ? ROMANIAN_BILLING_COUNTRY_CODE
        : '';
    if (!countryCode) {
        return { countryCode: '', region: '', city: '' };
    }
    const region = matchRomanianCounty(parts.region || '') || matchRomanianCounty(parts.city || '');
    if (!region) {
        return { countryCode, region: '', city: '' };
    }
    const candidates = [parts.city, parts.locality, parts.district, parts.placeName]
        .map((value) => value?.trim() || '')
        .filter((value, index, list) => value && list.indexOf(value) === index);
    for (const candidate of candidates) {
        const city = matchRomanianLocality(
            region,
            candidate,
            candidates.filter((hint) => hint !== candidate),
        );
        if (city) {
            return { countryCode, region, city };
        }
    }
    if (region === 'București') {
        const sector = bucharestSectorFromPostcode(parts.postalCode) || bucharestSectorAt(parts.longitude, parts.latitude);
        if (isRomanianLocality(region, sector)) {
            return { countryCode, region, city: sector };
        }
    }
    return { countryCode, region, city: '' };
};
